import { useEffect, useState, RefObject } from 'react';

/**
 * Hook to get dimensions of an element without triggering forced reflow.
 * Uses ResizeObserver for asynchronous, non-blocking dimension updates.
 */
export function useAsyncDimensions(ref: RefObject<HTMLElement | null>) {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!ref.current) return;

    const observer = new ResizeObserver((entries) => {
      requestAnimationFrame(() => {
        for (const entry of entries) {
          if (entry.contentRect) {
            // Asynchronous update, doesn't block the current frame
            setDimensions({
              width: entry.contentRect.width,
              height: entry.contentRect.height
            });
          }
        }
      });
    });

    observer.observe(ref.current);

    return () => {
      if (ref.current) observer.unobserve(ref.current);
      observer.disconnect();
    };
  }, [ref]);

  return dimensions;
}

/**
 * Example usage replacing offsetWidth:
 * 
 * const divRef = useRef<HTMLDivElement>(null);
 * const { width } = useAsyncDimensions(divRef);
 * 
 * // Instead of: const width = divRef.current?.offsetWidth;
 */

/**
 * Hook to detect visibility asycnronously (IntersectionObserver)
 */
export function useLazyVisibility(ref: RefObject<HTMLElement | null>, options?: IntersectionObserverInit) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!ref.current) return;

    const observer = new IntersectionObserver(([entry]) => {
      setIsVisible(entry.isIntersecting);
    }, options);

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, options]);

  return isVisible;
}
